import React, { useEffect, useState } from "react";
import { CalendarDays, Check } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { attendanceApi } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const STATUSES = ["Present", "Absent", "Late", "Half-day", "On leave"];
const statusStyle = {
  Present: "text-emerald-600", Absent: "text-red-500", Late: "text-amber-600",
  "Half-day": "text-sky-600", "On leave": "text-violet-600", "": "text-slate-400",
};

function SummaryCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 text-center">
      <div className={`font-display font-extrabold text-2xl ${color}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function Attendance() {
  const { toast } = useToast();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ counts: {} });

  const load = async (d) => {
    try {
      const [r, s] = await Promise.all([attendanceApi.list(d), attendanceApi.summary(d)]);
      setRows(r); setSummary(s);
    } catch (e) {}
  };
  useEffect(() => { load(date); }, [date]);

  const save = async (row, patch) => {
    const next = { ...row, ...patch };
    setRows((rs) => rs.map((x) => x.employee_id === row.employee_id ? next : x));
    try {
      await attendanceApi.upsert({ employee_id: row.employee_id, date, status: next.status || "Present", clock_in: next.clock_in || "", clock_out: next.clock_out || "", hours: Number(next.hours) || 0, note: next.note || "" });
      const s = await attendanceApi.summary(date); setSummary(s);
    } catch (e) { toast({ title: "Could not save", variant: "destructive" }); }
  };

  const c = summary.counts || {};
  return (
    <div className="pt-2 pb-6 animate-fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-navy">Attendance</h1>
          <p className="text-slate-500 mt-1">Mark daily attendance, working hours & rest — per the Act.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 px-3 h-11">
          <CalendarDays className="w-4 h-4 text-brand-blue" />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="outline-none text-sm text-slate-700 bg-transparent" />
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-6">
        <SummaryCard label="Present" value={c.Present || 0} color="text-emerald-600" />
        <SummaryCard label="Absent" value={c.Absent || 0} color="text-red-500" />
        <SummaryCard label="Late" value={c.Late || 0} color="text-amber-600" />
        <SummaryCard label="Half-day" value={c["Half-day"] || 0} color="text-sky-600" />
        <SummaryCard label="On leave" value={c["On leave"] || 0} color="text-violet-600" />
        <SummaryCard label="Unmarked" value={c.Unmarked || 0} color="text-slate-400" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/50">
            <th className="py-3 px-5 font-medium">Employee</th>
            <th className="py-3 px-3 font-medium">Status</th>
            <th className="py-3 px-3 font-medium">Clock in</th>
            <th className="py-3 px-3 font-medium">Clock out</th>
            <th className="py-3 px-3 font-medium">Hours</th>
          </tr></thead>
          <tbody>
            {rows.map((row) => {
              const initials = `${row.name?.[0] || ""}`;
              return (
                <tr key={row.employee_id} className="border-b border-slate-50 last:border-0 hover:bg-brand-mist/40">
                  <td className="py-2.5 px-5">
                    <div className="flex items-center gap-3">
                      {row.avatar ? <img src={row.avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-brand-light text-brand-blue flex items-center justify-center text-xs font-semibold">{initials}</div>}
                      <div><div className="font-semibold text-brand-navy">{row.name}</div><div className="text-xs text-slate-500">{row.department}</div></div>
                    </div>
                  </td>
                  <td className="py-2.5 px-3">
                    <Select value={row.status || ""} onValueChange={(v) => save(row, { status: v })}>
                      <SelectTrigger className={`w-32 h-9 font-medium ${statusStyle[row.status || ""]}`}><SelectValue placeholder="Mark" /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="py-2.5 px-3"><Input type="time" value={row.clock_in || ""} onChange={(e) => save(row, { clock_in: e.target.value })} className="w-28 h-9" /></td>
                  <td className="py-2.5 px-3"><Input type="time" value={row.clock_out || ""} onChange={(e) => save(row, { clock_out: e.target.value })} className="w-28 h-9" /></td>
                  <td className="py-2.5 px-3"><Input type="number" step="0.5" value={row.hours || ""} onChange={(e) => save(row, { hours: e.target.value })} className="w-20 h-9" placeholder="0" /></td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-slate-400">No active employees.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
